import { Link } from 'react-router-dom';
import { FileText, Users, Award, ChevronRight, Shield, Heart } from 'lucide-react';

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_d6850cf9-a07b-43f5-9597-bedbc8f7bac5/artifacts/cn4guv7s_7983314-uhd_2160_3840_24fps.mp4";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_vets-intake-demo/artifacts/e4mtecfo_Untitled%20design%20%2892%29.png";
const DONATION_URL = "https://fec1af13-e154-40f6-9732-ef75aabe7b90.paylinks.godaddy.com/d80cd3ea-dba0-4acc-9995-dc0";

// Professional veteran images
const IMAGES = {
  veteranGroup: "https://images.unsplash.com/photo-1763359838113-823ac5e75440?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHx2ZXRlcmFucyUyMGhlbHBpbmclMjBzdXBwb3J0JTIwZmFtaWx5JTIwbWlsaXRhcnl8ZW58MHx8fHwxNzczOTY5MzY3fDA&ixlib=rb-4.1.0&q=85",
  veteranSupport: "https://images.pexels.com/photos/7467907/pexels-photo-7467907.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  militaryUniform: "https://images.unsplash.com/photo-1682623764712-b73dcc36806a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHx2ZXRlcmFuJTIwbWlsaXRhcnklMjB1bmlmb3JtJTIwZGl2ZXJzZSUyMGFtZXJpY2FufGVufDB8fHx8MTc3Mzk2OTM2MXww&ixlib=rb-4.1.0&q=85",
  flagSalute: "https://images.pexels.com/photos/1340504/pexels-photo-1340504.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  familyReunion: "https://images.unsplash.com/photo-1674049406385-08fb37eeebd4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHx2ZXRlcmFucyUyMGhlbHBpbmclMjBzdXBwb3J0JTIwZmFtaWx5JTIwbWlsaXRhcnl8ZW58MHx8fHwxNzczOTY5MzY3fDA&ixlib=rb-4.1.0&q=85"
};

export const HomePage = () => {
  const services = [
    {
      icon: FileText,
      title: 'VA Disability Claims',
      description: 'Expert guidance through every step of filing or appealing your VA disability claim.',
      link: '/services#claims'
    },
    {
      icon: Shield,
      title: 'DBQ & Nexus Letters',
      description: 'Help obtaining the medical documentation you need to support your claim.',
      link: '/services#dbq'
    },
    {
      icon: Users,
      title: 'VA Process Support',
      description: 'Clear explanations and support to navigate the complex VA system with confidence.',
      link: '/services#support'
    }
  ];

  return (
    <article className="page-transition" data-testid="home-page">
      {/* Hero Section with Video Background */}
      <section className="relative min-h-[92vh] flex items-end pb-12 -mt-px" data-testid="hero-section" aria-label="Welcome to Worthy Vets Service Group">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: 'center 20%' }}
          data-testid="hero-video"
          aria-hidden="true"
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
        
        {/* Blue Overlay for text readability */}
        <div className="absolute inset-0 bg-wv-blue/70" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex flex-col items-center text-center">
            <img 
              src={LOGO_URL} 
              alt="Worthy Vets Service Group" 
              className="h-32 md:h-40 w-auto mb-8"
            />
            <p className="text-wv-red text-lg md:text-xl font-semibold mb-4 tracking-wide uppercase">
              Worthy Vets Service Group
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mb-6 uppercase tracking-wide">
              Because Every<br />Veteran is Worthy
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed max-w-3xl">
              You served our country. Now let us serve you. We help veterans navigate 
              the VA disability claims process with expertise, compassion, and dedication 
              to your success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/intake"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="hero-cta-primary"
              >
                Get Started
                <ChevronRight className="w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="btn-secondary inline-flex items-center justify-center"
                data-testid="hero-cta-secondary"
              >
                Learn About Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-wv-blue py-8" data-testid="trust-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-3 text-white">
              <Award className="w-8 h-8 text-wv-red" />
              <span className="text-lg font-semibold">Veteran-Owned</span>
            </div>
            <div className="flex items-center gap-3 text-white">
              <Shield className="w-8 h-8 text-wv-red" />
              <span className="text-lg font-semibold">Trusted Guidance</span>
            </div>
            <div className="flex items-center gap-3 text-white">
              <Users className="w-8 h-8 text-wv-red" />
              <span className="text-lg font-semibold">Dedicated Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-paper-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-wv-blue mb-4">
              How We Help Veterans
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our services are designed to make the VA claims process clearer and less stressful.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="card-tactile rounded-lg p-8 hover:shadow-lg transition-all"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-12 h-12 text-wv-red mb-6" strokeWidth={2} />
                <h3 className="font-heading text-2xl font-bold text-wv-blue mb-4">
                  {service.title}
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  {service.description}
                </p>
                <Link
                  to={service.link}
                  className="inline-flex items-center text-wv-red font-bold text-lg hover:text-red-700 transition-colors"
                >
                  Learn More
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/intake"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="services-cta"
            >
              Request Assistance Today
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-wv-blue mb-6">
                You Deserve The Benefits You Earned
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                The VA claims process can be overwhelming, confusing, and frustrating. 
                Many veterans give up or accept ratings that do not reflect their true 
                conditions. That is where we come in.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Clear, step-by-step guidance through the entire process',
                  'Help gathering and organizing your evidence',
                  'Support understanding what the VA needs from you',
                  'Assistance with appeals and rating increases'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-lg">
                    <div className="w-6 h-6 bg-wv-red rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                className="btn-secondary inline-flex items-center"
              >
                Learn Our Story
              </Link>
            </div>
            <div className="relative">
              <img
                src={IMAGES.veteranSupport}
                alt="Veterans in a support group session discussing their experiences"
                className="rounded-lg shadow-xl w-full"
                loading="lazy"
                width="600"
                height="400"
              />
              <div className="absolute -bottom-6 -left-6 bg-wv-red text-white p-6 rounded-lg shadow-lg max-w-xs hidden md:block">
                <p className="text-white font-bold text-lg mb-1">Our Mission</p>
                <p className="text-base text-white/90">
                  To ensure every veteran receives the support and benefits they rightfully earned.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-wv-blue" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get the Benefits You Deserve?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Take the first step today. Our intake process is simple, and we are here 
            to guide you every step of the way.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/intake"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="cta-primary-button"
            >
              Start Your Claim
              <ChevronRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="btn-secondary inline-flex items-center justify-center"
              data-testid="cta-secondary-button"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Shop Teaser */}
      <section className="py-16 bg-paper-white" data-testid="shop-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-wv-blue mb-4">
            Show Your Pride
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            Visit our shop for exclusive Worthy Vets merchandise.
          </p>
          <Link
            to="/shop"
            className="btn-secondary inline-flex items-center"
            data-testid="shop-link"
          >
            Visit Shop
            <ChevronRight className="w-5 h-5 ml-1" />
          </Link>
        </div>
      </section>

      {/* Newsletter Signup Section */}
      <section className="py-16 bg-gradient-to-r from-wv-blue to-blue-800" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-wv-red rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Stay Informed
          </h3>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for the latest veteran resources, VA updates, 
            tips for your claims process, and exclusive content from Worthy Vets.
          </p>
          <a
            href="http://eepurl.com/jCnhRE"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-wv-red text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition-colors shadow-lg"
            data-testid="newsletter-signup-button"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            Subscribe to Newsletter
          </a>
        </div>
      </section>

      {/* Support a Veteran Section */}
      <section className="py-16 bg-wv-red" data-testid="donation-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
              <Heart className="w-8 h-8 text-wv-red" />
            </div>
          </div>
          <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Support a Veteran
          </h3>
          <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
            Your generosity helps us continue our mission to ensure every veteran receives 
            the support and benefits they rightfully earned.
          </p>
          <p className="text-sm text-white/70 mb-8 max-w-xl mx-auto">
            Note: Worthy Vets Service Group is not a 501(c)(3) nonprofit. 
            Contributions are voluntary and not tax-deductible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={DONATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white text-wv-red px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="home-donate-button"
            >
              <Heart className="w-5 h-5" />
              Donate Now
            </a>
            <Link
              to="/donate"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-colors"
              data-testid="home-donate-learn-more"
            >
              Learn More
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
};
